import { createSlice } from '@reduxjs/toolkit';
import { createEmptyUser, UserInfo } from '../../models/user-info';
import { UserSessionAttributes } from '../../models/user-session-model';
import { UserAttributes, createUser } from '../../models/login-register-model';
import { ActiveChargeAttributes } from '../../models/active-charges-model';
import { UserPlanAssociation } from '../../models/user-plan-associations-model';
import { isEqual } from 'lodash';

// Helper function to get stack trace
const getStackTrace = () => {
  const stack = new Error().stack;
  // Remove the first few lines (Error constructor, getStackTrace, and the calling function)
  return stack?.split('\n').slice(3).join('\n');
};

interface UserData {
  profile: UserInfo;
  userObj: UserAttributes;
  session: UserSessionAttributes | undefined;
  activeCharges: ActiveChargeAttributes[] | null;
  driverPlan: UserPlanAssociation | null;
  hostPlan: UserPlanAssociation | null;
  isFreeTier: boolean;
  isModeChanging: boolean;
  hasShownChargeAgain: boolean;
  suppressChargeSummaryNavigation: boolean;
}

const initialState: UserData = {
  profile: createEmptyUser(),
  userObj: createUser(),
  session: undefined,
  activeCharges: null,
  driverPlan: null,
  hostPlan: null,
  isFreeTier: false,
  isModeChanging: false,
  hasShownChargeAgain: false,
  suppressChargeSummaryNavigation: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginUser(state, action) {
      //Receive a user's profile as a state when they log in
      state.profile = action.payload.profile;
    },
    setUserObj(state, action) {
      state.userObj = action.payload.userObj;
    },
    clearUserObj(state) {
      state.userObj = createUser();
    },
    setUserSession(state, action) {
      // Log every setUserSession call for debugging with stack trace
      // console.log('setUserSession called:', {
      //   oldSession: state.session?.primary_use,
      //   newSession: action.payload.session?.primary_use,
      // });
      // console.trace('setUserSession stack trace:');
      // Only update if the session actually changed
      if (!isEqual(state.session, action.payload.session)) {
        state.session = action.payload.session;
      }
    },
    setActiveCharges(state, action) {
      state.activeCharges = action.payload.activeCharges; // Set activeCharges in store
    },
    clearActiveCharges(state) {
      state.activeCharges = null; // Clear activeCharges
    },
    setDriverPlan(state, action) {
      state.driverPlan = action.payload.driverPlan;
    },
    setHostPlan(state, action) {
      const hostPlan: UserPlanAssociation | null =
        action.payload.hostPlan ?? null;
      state.hostPlan = hostPlan;
      // A user is on the free tier when their active host plan has no price.
      state.isFreeTier = hostPlan?.hostPlan?.price === 0;
    },
    setIsModeChanging(state, action) {
      state.isModeChanging = action.payload;
    },
    setHasShownChargeAgain(state, action) {
      state.hasShownChargeAgain = action.payload;
    },
    setSuppressChargeSummaryNavigation(state, action) {
      state.suppressChargeSummaryNavigation = action.payload;
    },
  },
});

export const userActions = userSlice.actions;
export default userSlice;
